import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { AlertCircle } from 'lucide-react';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate('/admin', { replace: true });
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/admin', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      else setError('Invalid credentials');
    }
    setSubmitting(false);
  };

  if (loading) return null;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: '#0a0a0a', fontFamily: "'Space Grotesk', sans-serif" }}
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link
            to="/"
            className="inline-block px-4 py-2 text-white font-bold text-2xl no-underline"
            style={{
              letterSpacing: '0.15em',
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(10px)',
            }}
            data-testid="admin-logo"
          >
            BCG
          </Link>
          <p
            className="mt-4 text-sm uppercase"
            style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em' }}
          >
            Admin Portal
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-8"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.1)',
            backdropFilter: 'blur(20px)',
          }}
          data-testid="admin-login-form"
        >
          {error && (
            <div
              className="mb-4 p-3 text-sm flex items-center gap-2"
              style={{
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.2)',
                color: '#f87171',
              }}
              data-testid="login-error"
            >
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="mb-4">
            <Label
              className="block mb-2 text-xs uppercase"
              style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}
            >
              Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@brandscapitalgroup.com"
              required
              data-testid="login-email"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/30"
            />
          </div>

          <div className="mb-6">
            <Label
              className="block mb-2 text-xs uppercase"
              style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}
            >
              Password
            </Label>
            <Input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              data-testid="login-password"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/30"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-white text-black hover:bg-white/90 font-semibold"
            style={{ letterSpacing: '0.05em' }}
            data-testid="login-submit"
          >
            {submitting ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
